import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import {
  VoucherSection,
  EmptyState,
  VoucherGrid,
  VoucherCard,
  VoucherPill,
  DiscountBlock,
  DiscountValue,
  DiscountMeta,
  VoucherDivider,
  VoucherInfo,
  VoucherInfoName,
  VoucherInfoPeriod,
  ModalOverlay,
  ModalContent,
  ModalTitle,
  ModalMessage,
  ModalActions,
  ModalButton,
  PageHeaderDiscount,
} from "../../styles/DiscountVouchers.styled";
import {VoucherContainer} from "../../styles/dashboard";
import { setItemWithObject } from "../../utils/cache/LocalStorage";
import moment from "moment";
import ResponsivePagination from "../../components/pagination/ResponsivePagination";
import { useSidebar } from "../../context/SidebarContext";
import {
  FigmaAddVoucherButton,
  FigmaBalanceDivider,
} from "../../styles/Wallet.styled";
import AddIcon from "@mui/icons-material/Add";
import {
  TotalDiscountTitle,
} from "../../styles/Reports.styled";
import { TabsContainer, Tab, TabsList } from "../../styles/OrderHistory.styled";
import { POST } from "../../services/HTTPServices";
import { useErrorResponse } from "../../services/useErrorResponse";
interface VoucherFormData {
  id: string;
  serviceProviderId: string;
  name: string;
  description: string;
  discountType: "fixed" | "percentage";
  type: "fixed" | "percentage";
  amount: number;
  miniumAmount: number;
  startTime: string;
  endTime: string;
  status: string;
  isActive: string;
}
type TabType = "ongoing" | "upcoming" | "expired";

const DiscountVouchers: React.FC = () => {
  const navigate = useNavigate();
    const outErrorResponse = useErrorResponse();
  
  const { isSidebarCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState<TabType>("ongoing");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] =
    useState<VoucherFormData | null>(null);
  const [auth, setAuth] = React.useState<any>(null);
  const [ongoingPageNo, setOngoingPageNo] = React.useState(1);
  const [upcomingPageNo, setUpcomingPageNo] = React.useState(1);
  const [expiredPageNo, setExpiredPageNo] = React.useState(1);
  const [ongoingTotalPageCount, setOngoingTotalPageCount] = React.useState(0);
  const [upcomingTotalPageCount, setUpcomingTotalPageCount] = React.useState(0);
  const [expiredTotalPageCount, setExpiredTotalPageCount] = React.useState(0);

  const [ongoingVouchers, setOngoingVouchers] = useState<VoucherFormData[]>([]);
  const [upcomingVouchers, setUpcomingVouchers] = useState<VoucherFormData[]>(
    []
  );
  const [expiredVouchers, setExpiredVouchers] = useState<VoucherFormData[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  // Add responsive detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);


  const getVouchers = async (id: number, type: string, page: any = null) => {

    let url = "voucher/GetVouchersByServiceProviderId";
    let pgNo =
      page ||
      (type === "ongoing"
        ? ongoingPageNo
        : type === "upcoming"
        ? upcomingPageNo
        : expiredPageNo);
    let param = {
      serviceProviderId: id.toString(),
      status: type,
      pgSize: 5,
      pgNo: pgNo,
    };
    let res: any = await POST(url, param, "order");
    if (res && res.status === 200) {
      const apiVouchers: VoucherFormData[] = Array.isArray(res.data?.vouchers)
        ? res.data.vouchers
        : [];
      const vouchers: VoucherFormData[] = apiVouchers;
      const totalPages = res.data?.retPage || 0;

      if (type === "ongoing") {
        setOngoingVouchers(vouchers);
        setOngoingTotalPageCount(totalPages);
      }
      if (type === "upcoming") {
        setUpcomingVouchers(vouchers);
        setUpcomingTotalPageCount(totalPages);
      }
      if (type === "expired") {
        setExpiredVouchers(vouchers);
        setExpiredTotalPageCount(totalPages);
      }
    } else {
      outErrorResponse(res.status, res.message);
    }
  };

  useEffect(() => {
    localStorage.removeItem("selected_voucher");
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setAuth(authUser);
      // Wait for isMobile state to be set before making API calls
      setTimeout(() => {
        getVouchers(authUser.id, "ongoing");
        getVouchers(authUser.id, "upcoming");
        getVouchers(authUser.id, "expired");
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const handleAddVoucher = () => {
    navigate("/discount-vouchers/create");
  };

  const confirmDelete = () => {
    setShowDeleteModal(false);
    if (selectedVoucher) {
      setItemWithObject("selected_voucher", selectedVoucher);
      navigate(`/discount-vouchers/edit/${selectedVoucher.id}`);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleEditVoucher = (voucher: VoucherFormData) => {
    if (voucher.status.trim().toLowerCase() === "upcoming") {
      setSelectedVoucher(voucher);
      setShowDeleteModal(true);
    }
  };

  const renderVoucherCard = (voucher: VoucherFormData) => {
    const status = voucher.status?.trim().toLowerCase();
    const isExpired = status === "expired";
    const isUpcoming = status === "upcoming";
    const isAmount = voucher.type?.trim().toLocaleLowerCase() === "amount";

    const valueText = isAmount ? `$${voucher.amount}` : `${voucher.amount}%`;

    return (
      <VoucherCard
        key={voucher.id}
        onClick={() => handleEditVoucher(voucher)}
        className={`${isUpcoming ? "pointer" : ""}`}
      >
        <VoucherPill $expired={isExpired}>
          <DiscountBlock $expired={isExpired}>
            <DiscountValue $expired={isExpired}>{valueText}</DiscountValue>
            {voucher.miniumAmount && (
              <DiscountMeta $expired={isExpired}>
                <div> Cap at ${voucher.miniumAmount}</div>
                <div> Min. Spend ${voucher.miniumAmount}</div>
              </DiscountMeta>
            )}
          </DiscountBlock>

          <VoucherDivider $expired={isExpired} />

          <VoucherInfo $expired={isExpired}>
            <VoucherInfoName $expired={isExpired}>
              {voucher.name}
            </VoucherInfoName>
            <VoucherInfoPeriod $expired={isExpired}>
              {`${moment(voucher.startTime).format(
                "DD/MM/YYYY h:mm A"
              )} - ${moment(voucher.endTime).format("DD/MM/YYYY h:mm A")}`}
            </VoucherInfoPeriod>
          </VoucherInfo>
        </VoucherPill>
      </VoucherCard>
    );
  };

  // Define type for better type safety
  type VoucherType = "upcoming" | "ongoing" | "expired";

  const pageChanged = (page: number, type: VoucherType) => {
    // Map of voucher types to their corresponding page setters
    const pageSetters = {
      upcoming: setUpcomingPageNo,
      ongoing: setOngoingPageNo,
      expired: setExpiredPageNo,
    };

    // Get the appropriate setter function
    const setPageNo = pageSetters[type];

    if (setPageNo) {
      setPageNo(page);
      getVouchers(auth.id, type, page);
    } else {
      console.warn(`Unknown voucher type: ${type}`);
    }
  };
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    getVouchers(auth.id, tab);
  };
  return (
    <>
      <VoucherContainer $isSidebarCollapsed={isSidebarCollapsed}>
        <PageHeaderDiscount>
          <FigmaAddVoucherButton onClick={handleAddVoucher}>
            <AddIcon />
            Add Voucher
          </FigmaAddVoucherButton>
        </PageHeaderDiscount>

        <TotalDiscountTitle>Discount Vouchers</TotalDiscountTitle>

        <FigmaBalanceDivider className="mb-8"/>

        <TabsContainer className="mb-32">
          <TabsList>
            <Tab
              $isActive={activeTab === "ongoing"}
              onClick={() => handleTabChange("ongoing")}
            >
              Current
            </Tab>
            <Tab
              $isActive={activeTab === "upcoming"}
              onClick={() => handleTabChange("upcoming")}
            >
              Upcoming
            </Tab>
            <Tab
              $isActive={activeTab === "expired"}
              onClick={() => handleTabChange("expired")}
            >
              Expired
            </Tab>
          </TabsList>
        </TabsContainer>

        {activeTab === "ongoing" ? (
          <VoucherSection>
            {ongoingVouchers.length === 0 ? (
              <EmptyState>No discount vouchers here.</EmptyState>
            ) : (
              <VoucherGrid>
                {ongoingVouchers.map(renderVoucherCard)}
              </VoucherGrid>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
              <ResponsivePagination
                currentPage={ongoingPageNo}
                totalPageCount={ongoingTotalPageCount}
                pageSize={5}
                onPageChange={(page: any) => pageChanged(page, "ongoing")}
              />
            </Box>
          </VoucherSection>
        ) : activeTab === "upcoming" ? (
          <VoucherSection>
            {upcomingVouchers.length === 0 ? (
              <EmptyState>No upcoming vouchers</EmptyState>
            ) : (
              <VoucherGrid>
                {upcomingVouchers.map(renderVoucherCard)}
              </VoucherGrid>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
              <ResponsivePagination
                currentPage={upcomingPageNo}
                totalPageCount={upcomingTotalPageCount}
                pageSize={5}
                onPageChange={(page: any) => pageChanged(page, "upcoming")}
              />
            </Box>
          </VoucherSection>
        ) : (
          <VoucherSection>
            {expiredVouchers.length === 0 ? (
              <EmptyState>No expired vouchers</EmptyState>
            ) : (
              <VoucherGrid>
                {expiredVouchers.map(renderVoucherCard)}
              </VoucherGrid>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
              <ResponsivePagination
                currentPage={expiredPageNo}
                totalPageCount={expiredTotalPageCount}
                pageSize={5}
                onPageChange={(page: any) => pageChanged(page, "expired")}
              />
            </Box>
          </VoucherSection>
        )}
      </VoucherContainer>

      {/* Delete Confirmation Modal */}
      <ModalOverlay $visible={showDeleteModal}>
        <ModalContent>
          <ModalTitle className="mb-16">Are you sure?</ModalTitle>
          <ModalMessage>
            These vouchers will be funded by merchant 100%.
          </ModalMessage>
          <ModalActions>
            <ModalButton $variant="secondary" onClick={cancelDelete}>
              Cancel
            </ModalButton>
            <ModalButton $variant="primary" onClick={confirmDelete}>
              Proceed
            </ModalButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default DiscountVouchers;
